import React, { useContext } from 'react';
import {
	UI,
	Utils,
	Modal,
	Feedback as FeedbackModule,
	CostCodes,
	Data,
	SubscriptionPlan,
	Chatbot,
} from 'common';
import { useFlags } from 'launchdarkly-react-client-sdk';

import DeleteContactsModal from './DeleteContactsModal.component';
import ReferenceNumberModal from './ReferenceNumberModal.component';
import MoveGroupModal from './groups/MoveGroupModal.component';
import ManageGroupsModal from './groups/ManageGroupsModal.component';
import ChangeCostCodeModal from './costCodes/ChangeCostCodeModal.component';
import ImportContactsModal from './importContacts/ImportContactsModal.component';

const { Logger, Session, BillingPlans, Analytics } = Utils;
const { WrappedToolbar, WrappedButtonType } = UI;
const { ModalContext } = Modal;
const { Feedback } = FeedbackModule;
const { ManageCostCodesModal } = CostCodes;
const { InternalCapability, Capability, PlanIds, SegmentEventTypes } = Data;
const { UpgradePlanModal } = SubscriptionPlan;
const { ChatbotInitializer, ChatbotTriggers, ChatbotMessengerState } = Chatbot;

const ContactsToolbar = ({
	groups,
	costCodes,
	selectedContacts,
	isAddingNewContact,
	onClickAdd,
	getCostCodes,
	fetchContacts,
	importContacts,
	onPrintPostageClick,
	deleteContacts,
	updateContactsState,
}) => {
	const { showModal } = useContext(ModalContext);
	const { chatbotFeedback } = useFlags();
	const canUseMultiAddressPrinting = Session.hasCapability(Capability.CanUseMultiAddressPrinting);
	const isNotEligibleForMultiPrint = !canUseMultiAddressPrinting && selectedContacts.length >= 2;
	const upgradeToOfficePlan = BillingPlans.getPlanByPlanId(PlanIds.OfficeV381);
	const { brandName } = Session.getBrand();
	const isEndicia = brandName === 'Endicia';

	const launchAddNewContact = () => {
		onClickAdd();
		Logger.logToServer({
			category: 'contacts_toolbar',
			page: 'contacts',
			action: 'click',
			ui_element: 'add_button',
		});
	};

	const launchDeleteModal = () => {
		showModal({
			component: DeleteContactsModal,
			data: {
				selectedContacts,
				deleteContacts,
			},
		});

		Logger.logToServer({
			category: 'contacts_toolbar',
			page: 'contacts',
			action: 'click',
			ui_element: 'delete_button',
		});
	};

	const onUpgradePlanCallback = async () => {
		const canPrintMultiAddress = Session.hasCapability(Capability.CanUseMultiAddressPrinting);

		// continue with print postage flow if upgrade is successful
		if (canPrintMultiAddress) {
			onPrintPostageClick();
		}
	};

	const segmentTracking = userAction => {
		const { customerDetails = {} } = Session.getAccountInfo();
		const customerEmail = customerDetails?.email;

		// EVENT USED BY MARKETING TEAM
		Analytics.trackEvent(
			'upgradePromptMultiAddressPrinting',
			{
				pointOfOrigin: 'contactsTab',
				userAction,
				contactsSelected: selectedContacts.length,
				email: customerEmail,
			},
			Analytics.agent.segment
		);
	};

	const trackSegmentUIEvent = dataqa => {
		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'button',
				field_name_dataqa: dataqa,
			},
			eventType: SegmentEventTypes.UserInteraction,
		});
	};

	const launchPrintPostage = () => {
		if (isNotEligibleForMultiPrint) {
			// show upgrade plan modal to office plan
			showModal({
				component: UpgradePlanModal,
				data: {
					upgradePlan: upgradeToOfficePlan,
					onUpgradePlanCallback,
					segmentTrackingCallback: segmentTracking,
				},
				closeCallback: () => {
					segmentTracking('closeModal');
				},
			});
		} else {
			onPrintPostageClick();
		}

		Logger.logToServer({
			category: 'choose_contact',
			method: 'contacts_print_button',
			contacts_count: selectedContacts.length,
			auto_suggest: false,
		});
	};

	const launchChangeGroupsModal = e => {
		const isDisabled = e.item.disabled;

		if (!isDisabled) {
			trackSegmentUIEvent(e.item.dataqa);
			showModal({
				component: MoveGroupModal,
				data: {
					selectedContacts,
					updateContactsState,
				},
			});
		}
	};

	const launchManageGroupsModal = e => {
		trackSegmentUIEvent(e.item.dataqa);
		showModal({
			component: ManageGroupsModal,
			data: {
				groups,
				callBack: fetchContacts,
			},
		});

		Logger.logToServer({
			category: 'groups_add_edit',
			action: 'click',
			action_location: 'contacts_toolbar',
		});
	};

	const launchChangeCostCodesModal = e => {
		const isDisabled = e.item.disabled;

		if (!isDisabled) {
			trackSegmentUIEvent(e.item.dataqa);

			showModal({
				component: ChangeCostCodeModal,
				data: {
					costCodes,
					selectedContacts,
					fetchContacts,
					updateContactsState,
				},
			});
		}
	};

	const launchManageCostCodesModal = e => {
		trackSegmentUIEvent(e.item.dataqa);

		showModal({
			component: ManageCostCodesModal,
			data: {
				callback: getCostCodes,
			},
		});

		Logger.logToServer({
			category: 'costcode_add_edit',
			action: 'click',
			action_location: 'contacts_toolbar',
		});
	};

	const launchReferenceModal = () => {
		showModal({
			component: ReferenceNumberModal,
			data: {
				updateContactsState,
				selectedContacts,
			},
		});

		Logger.logToServer({
			category: 'contacts_toolbar',
			page: 'contacts',
			action: 'click',
			ui_element: 'ref_button',
		});
	};

	const launchImportModal = () => {
		showModal({
			component: ImportContactsModal,
			data: {
				groups,
				importContacts,
				fetchContacts,
			},
		});

		Logger.logToServer({
			category: 'contacts_toolbar',
			action: 'click',
			ui_element: 'import_contacts',
		});
	};

	const launchExport = async () => {
		window.location = '/accountapi/v1/contacts/export';

		Logger.logToServer({
			category: 'contacts_toolbar',
			action: 'click',
			ui_element: 'export_contacts',
		});
	};

	const launchFeedbackModal = () => {
		if (brandName === 'Stamps.com' && chatbotFeedback) {
			ChatbotInitializer.start({
				chatbotState: ChatbotMessengerState.OPEN,
				logTrackEvent: true,
				trigger: ChatbotTriggers.FeedbackButton,
			});
		} else {
			showModal({
				component: Feedback,
			});
		}
	};

	const toolbarButtons = [
		{
			label: 'Add',
			icon: 'add-icon',
			onClick: launchAddNewContact,
			disabled: isAddingNewContact,
			tooltip: `${'Add'.bold()}\nAdd a new contact.`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'add',
		},
		{
			label: 'Delete',
			disabled: selectedContacts.length === 0,
			onClick: launchDeleteModal,
			tooltip: `${'Delete'.bold()}\nDelete selected contacts.`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'delete',
		},
		{
			label: 'Print Postage',
			icon: isNotEligibleForMultiPrint ? 'upgrade-icon' : null,
			className: isNotEligibleForMultiPrint ? 'upgrade-icon-btn' : null,
			disabled: selectedContacts.length === 0,
			onClick: launchPrintPostage,
			tooltip: isNotEligibleForMultiPrint
				? `${'Print Postage'.bold()}\nUpgrade your plan to Office to print multiple labels at once.`
				: `${'Print Postage'.bold()}\nPrint postage for a selected contact.`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'print-postage',
		},
		{
			label: 'Groups',
			tooltip: `${'Groups'.bold()}\nManage your contact groups.`,
			tooltipOptions: {
				position: 'bottom',
			},
			buttonType: WrappedButtonType.TertiaryGhost,
			dataqa: 'groups',
			model: [
				{
					label: 'Change Groups',
					disabled: selectedContacts.length === 0,
					command: launchChangeGroupsModal,
					dataqa: 'contacts-toolbar-change-groups-button',
				},
				{
					label: 'Add/Edit Groups',
					command: launchManageGroupsModal,
					dataqa: 'contacts-toolbar-add-edit-groups-button',
				},
			],
		},
		{
			label: 'Cost Codes',
			buttonType: WrappedButtonType.TertiaryGhost,
			tooltip: `${'Cost Codes'.bold()}\nManage the Cost Codes assigned to your contacts.`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'cost-codes',
			model: Session.hasCapability(InternalCapability.EditCostCodes)
				? [
						{
							label: 'Change Cost Code',
							disabled: selectedContacts.length === 0,
							command: launchChangeCostCodesModal,
							dataqa: 'contacts-toolbar-change-cost-codes-button',
						},
						{
							label: 'Add/Edit Cost Codes',
							command: launchManageCostCodesModal,
							dataqa: 'contacts-toolbar-add-edit-cost-codes-button',
						},
				  ]
				: [
						{
							label: 'Change Cost Code',
							disabled: selectedContacts.length === 0,
							command: launchChangeCostCodesModal,
							dataqa: 'contacts-toolbar-change-cost-code-button',
						},
				  ],
		},
		{
			label: 'Ref #',
			disabled: selectedContacts.length === 0,
			buttonType: WrappedButtonType.TertiaryGhost,
			onClick: launchReferenceModal,
			tooltip: `${'Ref #'.bold()}\nManage the Reference # assigned to your contacts.`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'ref#',
		},
		{
			icon: 'import-icon',
			buttonType: WrappedButtonType.TertiaryWhite,
			label: !isEndicia ? 'Import' : '',
			group: 'right',
			onClick: launchImportModal,
			tooltip: `${'Import'.bold()}\nImport contacts from a .csv or text file.`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'import',
		},
		{
			icon: 'export-icon',
			buttonType: WrappedButtonType.TertiaryWhite,
			label: !isEndicia ? 'Export' : '',
			group: 'right',
			onClick: launchExport,
			tooltip: `${'Export'.bold()}\nExport contacts to a .csv file.`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'export',
		},
		{
			icon: 'feedback-icon',
			buttonType: WrappedButtonType.TertiaryWhite,
			label: !isEndicia ? 'Feedback' : '',
			group: 'right',
			onClick: launchFeedbackModal,
			tooltip: `${'Feedback'.bold()}\nTell us how we're doing!`,
			tooltipOptions: {
				position: 'bottom',
			},
			dataqa: 'feedback',
		},
	];

	return (
		<div className="contacts-toolbar">
			<WrappedToolbar buttons={toolbarButtons} dataqa="contacts-toolbar" />
		</div>
	);
};

export default ContactsToolbar;
